import { useMeasurementsOverview } from "@api/generated/measurements/measurements";
import type {
  Coefficient,
  Descriptive,
  RelationsDraft,
  RelationsIn,
  RelationsResult,
  RelationVariable,
  SimpleRegression,
  VariableScopeDraft,
} from "@api/generated/model";
import { getRelationsExportUrl, relationsRun, useRelationsFields, useRelationsQueryColumns } from "@api/generated/relations/relations";
import { apiDownload } from "@api/http";
import { ONCE } from "@app/queryClient";
import { KEEP } from "@app/trials";
import { useAutosave, useSeason, useSeasonFromLink, useSettings } from "@app/workspace";
import { SavedQuerySelect, useSavedQuery } from "@features/queries/SavedQuerySelect";
import { pText, stars } from "@features/statistics/anova";
import { Scatter } from "@features/statistics/Scatter";
import { pickFields, sameScope, scopeOf, scopesForTrial, type Variable } from "@features/statistics/variables";
import { hint } from "@i18n";
import { AnimatedTabsList } from "@ui/AnimatedTabsList";
import "@features/statistics/statistics.css";
import { ActionIcon, Button, Loader, MultiSelect, SegmentedControl, Select, Tabs } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { formatNumber } from "@ui/format";
import { notifyError } from "@ui/notify";
import { PageHeader } from "@ui/PageHeader";
import { SheetPicker, useSheetInfo } from "@ui/SheetPicker";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import type { CSSProperties } from "react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const TABS = ["describe", "simple", "multiple", "correlation"] as const;
type Tab = (typeof TABS)[number];
const STAT_ROWS = ["n", "missing", "mean", "sd", "variance", "sem", "minimum", "maximum", "median", "cv", "ci_low", "ci_high"] as const;
const LEVELS = [0.9, 0.95, 0.99];
const EMPTY: RelationsDraft = { source: "trial", kis: null, sites: [], query: null, fields: [], scopes: [], confidence: 0.95 };

export const Route = createFileRoute("/analysis/relations")({
  validateSearch: z.object({ ev: z.number().int().optional(), tab: z.enum(TABS).optional() }),
  component: RelationsPage,
});

/** Elemzés és nyomtatás → Leíró statisztika és összefüggések (the legacy frmStatistics): numeric plot fields of a
 *  trial of the working season (its sites; joined on the plot key; a variable may be read at its own site, trial or
 *  year, then joined on the plot or the genotype) or a saved query's numeric columns, described, regressed (simple,
 *  multiple: the first variable is y) or correlated (a matrix; a cell opens its scatter plot). The choices are the
 *  user's draft (``relations_draft``). */
function RelationsPage() {
  const settings = useSettings();
  if (!settings) return <Loader size="sm" m="md" />;
  return <Relations stored={settings.relations_draft ?? null} />;
}

function Relations({ stored }: { stored: RelationsDraft | null }) {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const season = useSeasonFromLink(search.ev);
  const tab: Tab = search.tab ?? "correlation";
  const [draft, setDraft] = useState<RelationsDraft>(stored ?? EMPTY);
  useAutosave("relations_draft", draft, stored);
  const change = (p: Partial<RelationsDraft>) => setDraft((d) => ({ ...d, ...p }));
  // every trial measured this season (not only those with register plots: the values are read at the plots)
  const trials = useMeasurementsOverview(season ?? 0, { query: { enabled: !!season, ...KEEP } }).data ?? [];
  const trial = trials.find((x) => x.kis === draft.kis);
  const saved = useSavedQuery(draft.query, draft.source === "query");
  const trialSource = draft.source === "trial";
  const plotFields = useRelationsFields({ query: { staleTime: Number.POSITIVE_INFINITY } }).data ?? [];
  const queryColumns =
    useRelationsQueryColumns(draft.query ?? 0, { query: { enabled: draft.source === "query" && !!draft.query } }).data ?? [];
  const sheetInfo = useSheetInfo(draft.source === "sheet" ? draft.sheet : null);
  const choices =
    draft.source === "sheet"
      ? (sheetInfo.data?.numeric ?? []).map((id) => ({ id, label: id }))
      : draft.source === "trial"
        ? plotFields
        : queryColumns;
  const variables: Variable[] = (draft.fields ?? [])
    .map((field, i) => ({ field, scope: trialSource ? (draft.scopes?.[i] ?? null) : null }))
    .filter((v) => choices.some((c) => c.id === v.field))
    .slice(0, tab === "simple" ? 2 : 40);
  const fields = variables.map((v) => v.field);
  const scoped = variables.some((v) => v.scope);
  const setVariables = (next: Variable[]) => change({ fields: next.map((v) => v.field), scopes: next.map((v) => v.scope) });
  const source =
    draft.source === "sheet"
      ? draft.sheet
        ? { sheet: draft.sheet }
        : null
      : draft.source === "trial"
        ? season && draft.kis
          ? { trial: { ev: season, kis: draft.kis, sites: draft.sites ?? [] } }
          : null
        : draft.query
          ? { query: draft.query }
          : null;
  const body: RelationsIn | null =
    source && fields.length
      ? { analysis: tab, fields, confidence: draft.confidence, ...source, ...(scoped ? { scopes: variables.map((v) => v.scope) } : {}) }
      : null;
  const [asked, setAsked] = useState<RelationsIn | null>(null);
  const current = !!asked && !!body && JSON.stringify(asked) === JSON.stringify(body); // a changed choice dims the result
  const run = useQuery({ queryKey: ["relations", asked], queryFn: () => relationsRun(asked as RelationsIn), enabled: !!asked, ...ONCE });
  useEffect(() => {
    if (run.error) notifyError(run.error);
  }, [run.error]);
  const result = run.data; // after a change of the choices it stays, dimmed, until the next computation
  const ready = !!body && fields.length >= (tab === "describe" ? 1 : 2) && (tab !== "simple" || fields.length === 2);
  const title = draft.source === "trial" ? (draft.kis ?? "") : (saved.query?.name ?? "");
  const exportXlsx = () => {
    if (!body) return;
    const init = { method: "POST", body: JSON.stringify(body), headers: { "Content-Type": "application/json" } };
    apiDownload(getRelationsExportUrl({ title: title || t("relations.title") }), init, t("relations.fileName")).catch(notifyError);
  };
  return (
    <div className="page fill">
      <PageHeader
        title={t("relations.title")}
        subtitle={t("relations.subtitle")}
        season
        actions={
          <Button size="compact-sm" variant="default" disabled={!ready} onClick={exportXlsx}>
            {t("relations.excel")}
          </Button>
        }
      />
      <Tabs value={tab} onChange={(v) => v && void navigate({ search: (s) => ({ ...s, tab: v as Tab }) })}>
        <AnimatedTabsList mb="sm">
          {TABS.map((k) => (
            <Tabs.Tab key={k} value={k}>
              {t(`relations.tab.${k}`)}
            </Tabs.Tab>
          ))}
        </AnimatedTabsList>
      </Tabs>
      <div className="fill-row" style={{ alignItems: "flex-start" }}>
        <section className="card pad stack st-form" style={{ gap: 10 }}>
          <span className="sec">{t("relations.dataSource")}</span>
          <SegmentedControl
            size="xs"
            value={draft.source}
            onChange={(v) => change({ source: v as RelationsDraft["source"], fields: [], scopes: [] })}
            data={[
              { value: "trial", label: t("relations.trial") },
              { value: "query", label: t("queries.source.label") },
              { value: "sheet", label: t("parity.external") },
            ]}
          />
          {draft.source === "sheet" ? (
            <SheetPicker value={draft.sheet} onChange={(sheet) => change({ sheet, fields: [], scopes: [] })} />
          ) : draft.source === "trial" ? (
            <>
              <Select
                placeholder={hint("searchSelect")}
                size="xs"
                searchable
                label={t("relations.trialYear", { ev: season })}
                value={draft.kis ?? null}
                onChange={(v) => change({ kis: v, sites: [], scopes: scopesForTrial(draft.scopes ?? []) })}
                data={trials.map((x) => x.kis)}
              />
              {trial && trial.sites.length > 1 && (
                <MultiSelect
                  size="xs"
                  label={t("relations.sites")}
                  placeholder={t("relations.allSites")}
                  value={draft.sites ?? []}
                  onChange={(sites) => change({ sites })}
                  data={trial.sites}
                />
              )}
            </>
          ) : (
            <SavedQuerySelect value={draft.query ?? null} onChange={(id) => change({ query: id, fields: [], scopes: [] })} />
          )}
          <MultiSelect
            placeholder={hint("searchSelect")}
            size="xs"
            searchable
            label={t("relations.variables")}
            description={tab === "simple" || tab === "multiple" ? t("relations.firstIsY") : undefined}
            value={[...new Set(fields)]}
            onChange={(f) => setVariables(pickFields(variables, f))}
            maxValues={tab === "simple" ? 2 : 40}
            data={choices.map((c) => ({ value: c.id, label: c.label }))}
          />
          {trialSource && season && draft.kis && variables.length > 0 && (
            <Scopes
              variables={variables}
              choices={choices}
              season={season}
              kis={draft.kis}
              full={tab === "simple" && variables.length >= 2}
              onChange={setVariables}
            />
          )}
          {tab !== "correlation" && (
            <Select
              size="xs"
              label={t("relations.confidenceLevel")}
              value={String(draft.confidence)}
              allowDeselect={false}
              onChange={(v) => v && change({ confidence: Number(v) })}
              data={LEVELS.map((l) => ({ value: String(l), label: `${formatNumber(l * 100, 0)} %` }))}
            />
          )}
          <Button disabled={!ready} loading={run.isFetching} onClick={() => setAsked(body)}>
            {t("relations.run")}
          </Button>
        </section>
        <div className="stack grow" style={{ gap: 12, minWidth: 0 }}>
          {run.isFetching ? (
            <Loader size="sm" m="md" />
          ) : !result ? (
            <p className="sm mu inset">{t("relations.empty")}</p>
          ) : (
            <>
              {!current && (
                <div className="note warn sm hstack" style={{ gap: 10 }}>
                  <span className="grow">{t("relations.stale")}</span>
                  <Button size="compact-xs" disabled={!ready} onClick={() => setAsked(body)}>
                    {t("relations.recompute")}
                  </Button>
                </div>
              )}
              <div style={{ opacity: current ? 1 : 0.5 }}>
                <Result result={result} request={asked} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Where each of a trial's variables is read: the trial's own sites (the default), or its own site, trial or year;
 *  a variable can be taken again elsewhere (yield at MV against yield at KT). */
function Scopes(props: {
  variables: Variable[];
  choices: RelationVariable[];
  season: number;
  kis: string;
  full: boolean;
  onChange: (v: Variable[]) => void;
}) {
  const { t } = useTranslation();
  const label = new Map(props.choices.map((c) => [c.id, c.label]));
  const set = (i: number, scope: VariableScopeDraft | null) =>
    props.onChange(props.variables.map((v, j) => (j === i ? { ...v, scope } : v)));
  const taken = (v: Variable, i: number) => props.variables.some((w, j) => j !== i && w.field === v.field && sameScope(w.scope, v.scope));
  return (
    <div className="stack" style={{ gap: 6 }}>
      <span className="sec">{t("relations.placementTitle")}</span>
      <p className="sm mu">{t("relations.placementHelp")}</p>
      {props.variables.map((v, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a field may be listed twice; its place in the list is its identity
        <div key={i} className="stack" style={{ gap: 4 }}>
          <div className="hstack" style={{ gap: 4 }}>
            <b className="sm grow">{label.get(v.field) ?? v.field}</b>
            <ActionIcon
              size="sm"
              variant="subtle"
              aria-label={t("relations.duplicate")}
              title={t("relations.duplicate")}
              disabled={props.full}
              onClick={() =>
                props.onChange([...props.variables.slice(0, i + 1), { field: v.field, scope: v.scope }, ...props.variables.slice(i + 1)])
              }
            >
              +
            </ActionIcon>
            <ActionIcon
              size="sm"
              variant="subtle"
              aria-label={t("relations.remove")}
              title={t("relations.remove")}
              onClick={() => props.onChange(props.variables.filter((_w, j) => j !== i))}
            >
              ×
            </ActionIcon>
          </div>
          <ScopeRow scope={v.scope} season={props.season} kis={props.kis} error={taken(v, i)} onChange={(scope) => set(i, scope)} />
        </div>
      ))}
    </div>
  );
}

function ScopeRow(props: {
  scope: VariableScopeDraft | null;
  season: number;
  kis: string;
  error: boolean;
  onChange: (s: VariableScopeDraft | null) => void;
}) {
  const { t } = useTranslation();
  const { years } = useSeason();
  const ev = props.scope?.ev ?? props.season;
  const kis = props.scope?.kis ?? props.kis;
  const trials = useMeasurementsOverview(ev, { query: KEEP }).data ?? [];
  const sites = trials.find((x) => x.kis === kis)?.sites ?? [];
  const own = t("relations.ofTrial");
  const set = (p: Partial<VariableScopeDraft>) => {
    props.onChange(scopeOf({ ev: props.scope?.ev ?? null, kis: props.scope?.kis ?? null, th: props.scope?.th ?? null, ...p }));
  };
  return (
    <div className="hstack" style={{ gap: 4 }}>
      <Select
        size="xs"
        w={80}
        aria-label={t("relations.year")}
        placeholder={String(props.season)}
        clearable
        value={props.scope?.ev != null ? String(props.scope.ev) : null}
        onChange={(v) => set({ ev: v ? Number(v) : null, th: null })}
        data={years.filter((y) => y !== props.season).map(String)}
        error={props.error}
      />
      <Select
        size="xs"
        w={110}
        aria-label={t("relations.trialShort")}
        placeholder={props.kis}
        searchable
        clearable
        value={props.scope?.kis ?? null}
        onChange={(v) => set({ kis: v, th: null })}
        data={trials.map((x) => x.kis).filter((k) => k !== props.kis)}
        error={props.error}
      />
      <Select
        size="xs"
        w={90}
        aria-label={t("relations.place")}
        placeholder={own}
        clearable
        value={props.scope?.th ?? null}
        onChange={(v) => set({ th: v })}
        data={sites}
        error={props.error}
      />
    </div>
  );
}

function Result({ result: r, request }: { result: RelationsResult; request: RelationsIn | null }) {
  const { t } = useTranslation();
  const names = r.variables.map((v) => v.label);
  const head = (
    <p className="sm mu">
      {t(r.matched_by === "genotype" ? "relations.rowCountGenotype" : "relations.rowCount", { count: r.rows })}{" "}
      {(r.dropped ?? 0) > 0 && t("relations.unpaired", { count: r.dropped })} {r.truncated && <b>{t("relations.truncated")}</b>}
    </p>
  );
  if (r.describe)
    return (
      <section className="card stack" style={{ gap: 0 }}>
        <div className="panel-head">{head}</div>
        <Describe stats={r.describe} names={names} />
      </section>
    );
  if (r.simple)
    return (
      <section className="card pad stack" style={{ gap: 10 }}>
        {head}
        <Regression fit={r.simple} y={names[0] ?? ""} x={names[1] ?? ""} />
      </section>
    );
  if (r.multiple) {
    const m = r.multiple;
    return (
      <section className="card stack" style={{ gap: 0 }}>
        <div className="panel-head stack" style={{ gap: 4 }}>
          {head}
          <span>
            {t("relations.multipleFit", {
              y: names[0],
              r: formatNumber(m.r, 3),
              r2: formatNumber(m.r2 * 100, 1),
              adj: formatNumber(m.r2_adjusted * 100, 1),
              n: m.n,
            })}
          </span>
          <span className="sm mu">
            F = {formatNumber(m.anova[0]?.f, 2)} · p {pText(m.anova[0]?.p)} · SE {formatNumber(m.se, 3)}
          </span>
        </div>
        <Coefficients coefficients={m.coefficients} names={names} />
      </section>
    );
  }
  return r.correlation ? <Matrix result={r} request={request} head={head} /> : null;
}

type Described = { stats: Descriptive; name: string; i: number };

/** Each variable's descriptive statistics, a row each. */
function Describe(props: { stats: Descriptive[]; names: string[] }) {
  const { t } = useTranslation();
  const rows = useMemo(() => props.stats.map((stats, i) => ({ stats, name: props.names[i] ?? "", i })), [props.stats, props.names]);
  const columns = useMemo<Column<Described>[]>(
    () => [
      { id: "variable", header: t("relations.variable"), value: (r) => r.name },
      ...STAT_ROWS.map(
        (s): Column<Described> => ({
          id: s,
          header: t(`relations.stat.${s}`),
          value: (r) => r.stats[s],
          cell: (r) => (s === "n" || s === "missing" ? r.stats[s] : formatNumber(r.stats[s], 3)),
          numeric: true,
          className: "mono",
        }),
      ),
    ],
    [t],
  );
  const table = useDataTable("analysis.relations.describe", columns, rows);
  return <DataTable table={table} rowKey={(r) => r.i} />;
}

type Term = { c: Coefficient; name: string; i: number };

/** A multiple regression's terms (the intercept first): b, its SE and limits, the t test, standardized b, partial r. */
function Coefficients(props: { coefficients: Coefficient[]; names: string[] }) {
  const { t } = useTranslation();
  const rows = useMemo(
    () => props.coefficients.map((c, i) => ({ c, name: i === 0 ? t("relations.intercept") : (props.names[i] ?? ""), i })),
    [props.coefficients, props.names, t],
  );
  const columns = useMemo<Column<Term>[]>(
    () => [
      { id: "term", header: t("relations.term"), value: (r) => r.name },
      { id: "b", header: "b", value: (r) => r.c.b, cell: (r) => formatNumber(r.c.b, 4), numeric: true, className: "mono" },
      { id: "se", header: "SE", value: (r) => r.c.se, cell: (r) => formatNumber(r.c.se, 4), numeric: true, className: "mono" },
      {
        id: "limits",
        header: t("relations.confidenceLimits"),
        value: (r) => r.c.low,
        text: (r) => `${formatNumber(r.c.low, 4)} – ${formatNumber(r.c.high, 4)}`,
        numeric: true,
        className: "mono",
      },
      { id: "t", header: "t", value: (r) => r.c.t, cell: (r) => formatNumber(r.c.t, 2), numeric: true, className: "mono" },
      {
        id: "p",
        header: "p",
        value: (r) => r.c.p,
        cell: (r) => `${pText(r.c.p)} ${stars(r.c.p)}`,
        numeric: true,
        className: "mono",
      },
      {
        id: "standardized",
        header: t("relations.standardizedB"),
        value: (r) => r.c.standardized,
        cell: (r) => formatNumber(r.c.standardized, 3),
        numeric: true,
        className: "mono",
      },
      {
        id: "partial_r",
        header: t("relations.partialR"),
        value: (r) => r.c.partial_r,
        cell: (r) => formatNumber(r.c.partial_r, 3),
        numeric: true,
        className: "mono",
      },
    ],
    [t],
  );
  const table = useDataTable("analysis.relations.coefficients", columns, rows);
  return <DataTable table={table} rowKey={(r) => r.i} />;
}

/** The correlation matrix as a heat map; a cell opens the pair's regression (of the variables the matrix shows). */
function Matrix(props: { result: RelationsResult; request: RelationsIn | null; head: ReactNode }) {
  const { t } = useTranslation();
  const r = props.result;
  const c = r.correlation;
  const [cell, setCell] = useState<[number, number] | null>(null);
  const scopes = props.request?.scopes;
  const pairBody =
    cell && props.request
      ? {
          ...props.request,
          analysis: "simple" as const,
          fields: [r.variables[cell[0]]?.id ?? "", r.variables[cell[1]]?.id ?? ""],
          ...(scopes?.length ? { scopes: [scopes[cell[0]] ?? null, scopes[cell[1]] ?? null] } : {}),
        }
      : null;
  const pair = useQuery({
    queryKey: ["relations", pairBody],
    queryFn: () => relationsRun(pairBody as RelationsIn),
    enabled: !!pairBody,
    ...ONCE,
  });
  useEffect(() => {
    if (pair.error) notifyError(pair.error);
  }, [pair.error]);
  if (!c) return null;
  const names = r.variables.map((v) => v.label);
  return (
    <>
      <section className="card pad stack" style={{ gap: 10 }}>
        <div className="hstack" style={{ gap: 8 }}>
          <b className="grow">{t("relations.pearson", { n: r.rows })}</b>
          <span className="sm mu">{t("relations.legend")}</span>
        </div>
        {props.head}
        <div className="scroll">
          <table className="st-heat">
            <thead>
              <tr>
                <th />
                {names.map((n, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: labels and ids may repeat; the column's place is fixed
                  <th key={i} className="sm">
                    {n}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {c.r.map((row, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: a field may be analysed at two places
                <tr key={i}>
                  <th className="sm">{names[i]}</th>
                  {row.map((v, j) => {
                    const p = c.p[i]?.[j];
                    return (
                      // biome-ignore lint/suspicious/noArrayIndexKey: a field may be analysed at two places
                      <td key={j}>
                        <button
                          type="button"
                          className={v == null ? "st-cell empty" : v >= 0 ? "st-cell pos" : "st-cell neg"}
                          style={v == null ? undefined : ({ "--r": String(v) } as CSSProperties)}
                          disabled={i === j || v == null}
                          title={
                            v == null ? t("relations.tooFew") : t("relations.cell", { r: formatNumber(v, 3), p: pText(p), n: c.n[i]?.[j] })
                          }
                          onClick={() => setCell([i, j])}
                        >
                          {v == null ? "—" : `${formatNumber(v, 2)}${i === j ? "" : ` ${stars(p)}`}`}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="sm mu">{t("relations.clickCellHint")}</p>
      </section>
      {cell && (
        <section className="card pad stack" style={{ gap: 8 }}>
          {pair.data?.simple ? (
            <Regression fit={pair.data.simple} y={names[cell[0]] ?? ""} x={names[cell[1]] ?? ""} />
          ) : (
            <Loader size="sm" />
          )}
        </section>
      )}
    </>
  );
}

function Regression(props: { fit: SimpleRegression; y: string; x: string }) {
  const { t } = useTranslation();
  const { fit } = props;
  const sign = fit.intercept < 0 ? "−" : "+";
  return (
    <>
      <b>{`${props.y} = ${formatNumber(fit.slope, 4)} × ${props.x} ${sign} ${formatNumber(Math.abs(fit.intercept), 3)}`}</b>
      <span className="sm mu">
        {t("relations.fit", {
          r: formatNumber(fit.r, 3),
          stars: stars(fit.slope_p),
          r2: formatNumber(fit.r2, 3),
          se: formatNumber(fit.se, 3),
          n: fit.n,
        })}
        {fit.r_low != null && ` · ${t("relations.rLimits", { rl: formatNumber(fit.r_low, 3), rh: formatNumber(fit.r_high, 3) })}`}
        {fit.points.length < fit.n && ` · ${t("relations.sample", { count: fit.points.length })}`}
      </span>
      <Scatter fit={fit} y={props.y} x={props.x} />
      <p className="sm mu">{t("relations.bandsHelp")}</p>
    </>
  );
}
